// Care-team chat surface — premium, editorial messaging between member
// and physician. Realtime via WS (message.new event); optimistic send
// so messages feel instant on slow connections.
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Button from "@/components/Button";
import { Spinner } from "@/components/Loader";
import { apiGet, apiPost } from "@/lib/api";
import { useEvent, useResyncOnVisible } from "@/lib/realtime";
import { toast } from "@/components/Toast";
import { titleCase } from "@/lib/format";
import { C } from "@/lib/brand";

// Safety-net poll only — WS is the primary signal. Catches WS-drop
// scenarios where the reconnect didn't fire for some reason.
const SAFETY_POLL_MS = 5 * 60 * 1000;

export default function CareTeamChat() {
  const { consultationId } = useParams();
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [pendingMessages, setPendingMessages] = useState([]); // optimistic
  const scrollRef = useRef(null);

  const loadThread = async () => {
    try {
      // Try the consultation-scoped endpoint first; fall back to the
      // legacy unscoped one for backwards compatibility.
      let t;
      if (consultationId) {
        try {
          t = await apiGet(`/consumer/care-team/${consultationId}/messages`);
        } catch (err) {
          if (err.status === 404) t = await apiGet("/consumer/messages");
          else throw err;
        }
      } else {
        t = await apiGet("/consumer/messages");
      }
      // Accept a handful of backend shapes:
      //   { messages: [...], physician }         — canonical
      //   [...]                                  — bare array of messages
      //   { items: [...] } / { thread: [...] }    — common alternates
      //   { data: { messages: [...] } }           — wrapped
      let normalized = t;
      if (Array.isArray(t)) normalized = { messages: t };
      else if (t && Array.isArray(t.items)) normalized = { ...t, messages: t.items };
      else if (t && Array.isArray(t.thread)) normalized = { ...t, messages: t.thread };
      else if (t && t.data && Array.isArray(t.data.messages)) normalized = { ...t, ...t.data };
      else if (t && !Array.isArray(t.messages)) normalized = { ...t, messages: [] };
      setThread(normalized || { messages: [] });
      try { await apiPost("/consumer/messages/mark-read", { consultationId }); } catch {}
    } catch {
      setThread({ messages: [] });
    }
  };

  useEffect(() => {
    loadThread();
    // Safety-net poll only — WS is the primary signal.
    const id = setInterval(() => {
      if (document.visibilityState === "visible") loadThread();
    }, SAFETY_POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consultationId]);

  // Realtime: append in-place when a new message lands for this thread.
  // Filter by consultationId so we don't churn state when an unrelated
  // thread receives traffic. Dedupe by message.id in case the BE sends
  // the same message twice (or our optimistic copy beat the server).
  useEvent("message.new", (evt) => {
    if (!evt?.message?.id) return;
    if (consultationId && evt.consultationId && evt.consultationId !== consultationId) return;
    setThread((prev) => {
      const cur = prev || { messages: [] };
      const list = Array.isArray(cur.messages) ? cur.messages : [];
      // Dedupe by id — also drop any optimistic/pending entries that
      // share text + sender (server-assigned id won't match our temp).
      if (list.some((m) => (m._id || m.id) === evt.message.id)) return cur;
      return { ...cur, messages: [...list, evt.message] };
    });
    // Drop optimistic bubble that matches this server message text.
    setPendingMessages((p) => p.filter((m) => m.text !== (evt.message.textContent || evt.message.text)));
    // Mark read since the user is on the thread.
    if (document.visibilityState === "visible") {
      apiPost("/consumer/messages/mark-read", { consultationId }).catch(() => {});
    }
  });

  useResyncOnVisible(loadThread);

  // Auto-scroll on every thread change.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
  }, [thread, pendingMessages]);

  const physician = thread?.physician || thread?.assignedPhysician || null;
  const physicianName = physicianDisplay(physician);
  const initials = physicianInitials(physician);

  // Merge real + optimistic messages, dedupe by id when server echoes the
  // pending message back.
  const messages = useMemo(() => {
    const real = thread?.messages || [];
    const realIds = new Set(real.map((m) => m.id || m._id));
    const stillPending = pendingMessages.filter((p) => !realIds.has(p.id));
    return [...real, ...stillPending];
  }, [thread, pendingMessages]);

  const grouped = useMemo(() => groupByDay(messages), [messages]);

  const send = async (e) => {
    e?.preventDefault();
    const value = text.trim();
    if (!value || sending) return;
    const tempId = `temp_${Date.now()}`;
    const optimistic = {
      id: tempId,
      sender: "consumer",
      text: value,
      createdAt: new Date().toISOString(),
      deliveryStatus: "sending",
    };
    setPendingMessages((prev) => [...prev, optimistic]);
    setText("");
    setSending(true);
    try {
      await apiPost("/consumer/messages", {
        text: value,
        textContent: value, // some backends prefer this key
        consultationId,
        messageType: "text",
      });
      // No forced refetch — WS broadcasts the server-confirmed message
      // back to us via the `message.new` event handler, which dedupes
      // by id and drops the optimistic bubble. Falls through to the
      // safety-net poll if WS is degraded.
      setPendingMessages((prev) => prev.map((m) => m.id === tempId ? { ...m, deliveryStatus: "sent" } : m));
    } catch (err) {
      setPendingMessages((prev) => prev.map((m) => m.id === tempId ? { ...m, deliveryStatus: "failed" } : m));
      toast(err.detail || "Couldn't send. Tap to retry.", "error");
    } finally {
      setSending(false);
    }
  };

  if (!thread) {
    return <div style={{ padding: 64, textAlign: "center" }}><Spinner /></div>;
  }

  return (
    <div data-testid="care-team-chat" style={{ background: C.cream, display: "flex", flexDirection: "column", minHeight: "100dvh", height: "100dvh" }}>
      {/* Sticky chat header */}
      <header
        data-testid="care-team-header"
        style={{
          background: C.ink,
          color: C.cream,
          padding: "16px 20px",
          display: "flex",
          alignItems: "center",
          gap: 14,
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <Link to="/app" data-testid="chat-back" aria-label="Back" style={{ color: C.cream, fontSize: 20, textDecoration: "none", lineHeight: 1 }}>←</Link>
        <span
          aria-hidden
          style={{
            width: 36, height: 36, borderRadius: "50%",
            background: C.peach, color: C.ink,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            fontSize: 12, fontWeight: 800, letterSpacing: "0.06em",
          }}
        >
          {initials}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, letterSpacing: "-0.01em" }}>
            {physicianName || "Your care team"}
          </div>
          <div style={{ fontSize: 11, color: "rgba(250,246,240,0.55)", letterSpacing: "0.05em" }}>
            {physician?.title || "Your physician"}
            {thread?.physicianOnline ? " · Online" : ""}
          </div>
        </div>
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.2em", color: C.peach }}>CARE TEAM</span>
      </header>

      {/* Thread */}
      <div ref={scrollRef} data-testid="chat-thread" style={{ flex: 1, padding: "32px 18px 16px", overflowY: "auto", background: C.cream }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          {messages.length === 0 ? (
            <ChatEmpty physicianName={physicianName} />
          ) : (
            grouped.map((group) => (
              <div key={group.day}>
                <div data-testid={`chat-divider-${group.day}`} style={{ display: "flex", alignItems: "center", gap: 14, margin: "18px 0", color: C.warmGray, fontSize: 10, fontWeight: 800, letterSpacing: "0.2em" }}>
                  <span style={{ flex: 1, height: 0.5, background: C.warmGray3 }} />
                  <span>{group.label}</span>
                  <span style={{ flex: 1, height: 0.5, background: C.warmGray3 }} />
                </div>
                {group.messages.map((m) => (
                  <Bubble key={m.id || m._id} message={m} initials={initials} senderName={physicianName} />
                ))}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Compose bar */}
      <form
        onSubmit={send}
        data-testid="chat-compose"
        style={{
          background: C.pure,
          borderTop: `1px solid ${C.warmGray3}`,
          padding: "12px 14px calc(12px + env(safe-area-inset-bottom))",
          // flex-shrink:0 + parent height:100dvh guarantees the compose
          // bar sits at the real viewport bottom even when the soft
          // keyboard changes the visualViewport on mobile Safari.
          flexShrink: 0,
        }}
      >
        <div style={{ maxWidth: 720, margin: "0 auto", display: "flex", gap: 10, alignItems: "flex-end" }}>
          <textarea
            data-testid="chat-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder={physicianName ? `Message ${physicianName}…` : "Message your care team…"}
            rows={1}
            style={{
              flex: 1,
              minHeight: 44,
              maxHeight: 140,
              padding: "12px 14px",
              background: C.cream,
              border: `1.5px solid ${C.warmGray3}`,
              // Must be ≥16px on mobile Safari or focusing the field
              // triggers an automatic page-zoom that breaks the layout.
              fontSize: 16,
              color: C.ink,
              outline: "none",
              resize: "none",
              fontFamily: "inherit",
              lineHeight: 1.4,
            }}
          />
          <Button
            type="submit"
            variant="primary"
            testId="chat-send-btn"
            disabled={sending || !text.trim()}
          >
            {sending ? "…" : "SEND →"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function ChatEmpty({ physicianName }) {
  return (
    <div data-testid="chat-empty" style={{ padding: "44px 28px", textAlign: "center", color: C.warmGray }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.peachDeep }}>★ START THE CONVERSATION</div>
      <p style={{ marginTop: 14, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic", fontSize: 22, lineHeight: 1.4, color: C.ink }}>
        Send your first message to {physicianName || "your physician"}.
      </p>
      <p style={{ marginTop: 10, fontSize: 13, lineHeight: 1.55 }}>
        Most replies land within 24 hours. For emergencies, please call 911.
      </p>
    </div>
  );
}

function Bubble({ message, initials, senderName }) {
  const me = (message.sender || message.senderType) === "consumer" || (message.sender || message.senderType) === "member";
  const failed = message.deliveryStatus === "failed";
  const sending = message.deliveryStatus === "sending";
  const time = message.createdAt || message.sentAt;

  return (
    <div
      data-testid={`chat-msg-${me ? "me" : "physician"}`}
      style={{
        display: "flex",
        justifyContent: me ? "flex-end" : "flex-start",
        gap: 10,
        margin: "8px 0",
        opacity: sending ? 0.7 : 1,
      }}
    >
      {!me && (
        <span
          aria-hidden
          style={{
            width: 28, height: 28, borderRadius: "50%",
            background: C.ink, color: C.peach,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            fontSize: 10, fontWeight: 800, letterSpacing: "0.05em",
            flexShrink: 0, marginTop: 4,
          }}
        >
          {initials}
        </span>
      )}
      <div style={{ display: "flex", flexDirection: "column", maxWidth: "75%" }}>
        <div
          style={{
            background: me ? C.peach : C.pure,
            color: C.ink,
            border: me ? "none" : `1px solid ${C.warmGray3}`,
            padding: "12px 16px",
            fontSize: 15,
            lineHeight: 1.5,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {message.text || message.textContent || ""}
        </div>
        <div
          style={{
            marginTop: 4,
            fontSize: 10,
            color: failed ? C.coral : C.warmGray,
            letterSpacing: "0.05em",
            textAlign: me ? "right" : "left",
          }}
        >
          {failed ? "FAILED · TAP TO RETRY" : sending ? "Sending…" : time ? formatTime(time) : ""}
          {!me && message.read === false ? "" : ""}
          {me && message.read ? " · ✓ Read" : me && !sending && !failed ? " · ✓ Sent" : ""}
        </div>
      </div>
    </div>
  );
}

/* ----- helpers ----- */
function groupByDay(messages) {
  const out = [];
  let last = null;
  for (const m of messages) {
    const d = new Date(m.createdAt || m.sentAt || Date.now());
    const day = d.toDateString();
    if (day !== last) {
      out.push({ day, label: dayLabel(d), messages: [] });
      last = day;
    }
    out[out.length - 1].messages.push(m);
  }
  return out;
}
function dayLabel(d) {
  const today = new Date();
  const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "TODAY";
  if (d.toDateString() === yesterday.toDateString()) return "YESTERDAY";
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }).toUpperCase();
}
function formatTime(iso) {
  try { return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }); } catch { return ""; }
}
function physicianDisplay(p) {
  if (!p) return null;
  const last = p.lastName || p.surname;
  if (!last) return p.fullName || p.name || null;
  return `Dr. ${titleCase(last)}`;
}
function physicianInitials(p) {
  if (!p) return "MD";
  const f = (p.firstName || "").trim()[0];
  const l = (p.lastName || "").trim()[0];
  if (f && l) return `${f}${l}`.toUpperCase();
  return (p.initials || "MD").slice(0, 2).toUpperCase();
}
